import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../components/Header";
import MarkdownEditor from "../../components/MarkdownEditor";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import useGraphQLClient from "../../hooks/useGraphQLClient";

import { READ_INTERACTION_BY_ID } from "../../graph/interactions/queries";
import { UPDATE_INTERACTION } from "../../graph/interactions/mutations";
import {
  interactionTypeOptions,
  interactionDirectionOptions,
  valueActionOptions,
  interactionTypeValues,
  interactionDirectionValues,
  valueActionValues,
} from "../../config/exportFormOptions.js";

const validationSchema = yup.object().shape({
  type: yup.string().oneOf(interactionTypeValues).required("Required"),
  direction: yup.string().oneOf(interactionDirectionValues).required("Required"),
  valueAction: yup.string().oneOf(["", ...valueActionValues]).nullable().notRequired(),
  summary: yup.string().required("Required"),
  notes: yup.string().nullable().notRequired(),
  meaningful: yup.boolean().required(),
});

const EditInteractionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const client = useGraphQLClient();
  const redirectPath = location.state?.redirectPath || "/manageInteractions";

  const [initialValues, setInitialValues] = useState(null);
  const { setSnackbar } = useSnackbar();

  useEffect(() => {
    if (!id) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "No interaction id found in route",
      });
      return;
    }

    const fetchInteraction = async () => {
      try {
        const { readInteractionByID } = await client.request(
          READ_INTERACTION_BY_ID,
          { id }
        );

        setInitialValues({
          id: readInteractionByID.id,
          contactID: readInteractionByID.contactID || "",
          type: readInteractionByID.type || "",
          direction: readInteractionByID.direction || "",
          valueAction: readInteractionByID.valueAction || "",
          summary: readInteractionByID.summary || "",
          notes: readInteractionByID.notes || "",
          meaningful: Boolean(readInteractionByID.meaningful),
        });
      } catch (err) {
        console.error("Error fetching interaction:", err);
        console.error("GraphQL response errors:", err.response?.errors);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load interaction",
        });
      }
    };

    fetchInteraction();
  }, [id, client, setSnackbar]);

  const handleSubmit = async (values) => {
    try {
      const input = {
        id: values.id,
        type: values.type,
        direction: values.direction,
        valueAction: values.valueAction || null,
        summary: values.summary,
        notes: values.notes || null,
        meaningful: values.meaningful,
      };

      await client.request(UPDATE_INTERACTION, { input });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Interaction updated successfully",
      });

      navigate(redirectPath);
    } catch (err) {
      console.error("Update failed:", err);
      console.error("GraphQL response errors:", err.response?.errors);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update interaction",
      });
    }
  };

  if (!initialValues) {
    return <div>Loading...</div>;
  }

  return (
    <Box>
      <Header title="EDIT INTERACTION" subtitle="Edit interaction details" />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          touched,
          errors,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="20px" gridTemplateColumns="repeat(4, 1fr)">
              <TextField
                label="Contact ID"
                name="contactID"
                value={values.contactID}
                disabled
                sx={{ gridColumn: "span 4" }}
              />

              <FormControl
                fullWidth
                sx={{ gridColumn: "span 2" }}
                error={!!touched.type && !!errors.type}
              >
                <InputLabel>Interaction Type</InputLabel>
                <Select
                  name="type"
                  value={values.type}
                  label="Interaction Type"
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {interactionTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                sx={{ gridColumn: "span 2" }}
                error={!!touched.direction && !!errors.direction}
              >
                <InputLabel>Direction</InputLabel>
                <Select
                  name="direction"
                  value={values.direction}
                  label="Direction"
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {interactionDirectionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                sx={{ gridColumn: "span 4" }}
                error={!!touched.valueAction && !!errors.valueAction}
              >
                <InputLabel>Value Action</InputLabel>
                <Select
                  name="valueAction"
                  value={values.valueAction}
                  label="Value Action"
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <MenuItem value="">None</MenuItem>
                  {valueActionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Summary"
                name="summary"
                value={values.summary}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.summary && !!errors.summary}
                helperText={touched.summary && errors.summary}
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.notes}
                onChange={(nextValue) => setFieldValue("notes", nextValue)}
                label="Notes"
                minRows={14}
                error={!!touched.notes && !!errors.notes}
                helperText={touched.notes && errors.notes}
                sx={{ gridColumn: "span 4" }}
              />

              <Box sx={{ gridColumn: "span 4" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.meaningful}
                      onChange={(e) => setFieldValue("meaningful", e.target.checked)}
                    />
                  }
                  label="Meaningful interaction"
                />
              </Box>
            </Box>

            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Button type="submit" variant="contained" color="secondary">
                Save Changes
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default EditInteractionForm;